import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { modal } from "commonAssets/lib/modal";
import { loading } from "commonAssets/js/constants";
import { customSubmitClaim } from "../SubmitClaim/index.mjs";
import { customSubmitFutureCoverageClaim } from "../SubmitFutureCoverageClaim/index.mjs";

/**
 * Checks whether the logged-in claim was filed via the Future Coverage form.
 */
function isFutureCoverageClaim(class_member) {
    const formTypeField = (class_member.claim_fields || []).find(
        (field) => field.name === "Form Type"
    );
    return Boolean(formTypeField && formTypeField.value === "Future Coverage");
}

/**
 * Deficiency gating. Only allows class members with an active, editable
 * "Deficient" ClassMemberStatus whose deadline has not passed.
 *
 * @returns {string|null} Error message, or null when access is allowed.
 */
function deficiencyGateError(class_member) {
    // - If claim_id exists, data is nested under class_member
    // - Otherwise data is directly on class_member
    const ClassMemberStatus = Boolean(class_member.claim_id)
        ? class_member.class_member?.cm_data?.ClassMemberStatus
        : class_member.cm_data?.ClassMemberStatus;

    if (!Array.isArray(ClassMemberStatus)) {
        return "This portal is not available for new claims.";
    }

    const status = ClassMemberStatus.find(
        (item) => item.Label === "Deficient"
    );

    if (status && status.Deadline && new Date() > new Date(status.Deadline)) {
        return "The deadline to Fix Claim has passed.";
    }

    if (!status || !status.Active || !status.CanEdit) {
        return "This portal is not available for claim edits.";
    }

    return null;
}

/**
 * Adds the hidden "Deficiency Response" field to the first claim field
 * section of a form config (fix-claim portal only; the submit-claim forms
 * never send it).
 */
function withDeficiencyResponseField(form) {
    const field = {
        type: "hidden",
        label: "Deficiency Response",
        name: "deficiency_response",
        parentClass: "d-none",
    };
    const [firstSection, ...otherSections] = form.claim_fields_sectioned || [];

    if (!firstSection) {
        return {
            ...form,
            claim_fields: [field, ...(form.claim_fields || [])],
        };
    }

    return {
        ...form,
        claim_fields_sectioned: [
            { ...firstSection, fields: [field, ...firstSection.fields] },
            ...otherSections,
        ],
    };
}

/**
 * Marks the claim as a deficiency response. Runs after loadMember() so a
 * previously saved value can't blank it.
 */
function setDeficiencyResponse() {
    const deficiencyResponse = document.getElementById("deficiency_response");
    if (deficiencyResponse) {
        deficiencyResponse.value = "YES";
    }
}

/**
 * Future Coverage form used by the fix-claim portal. It never renders its own
 * guard; `form()` only resolves the form config so `customFixClaim` can hand
 * off a logged-in Future Coverage claimant to it.
 */
class customFixFutureCoverageClaim extends customSubmitFutureCoverageClaim {
    init_claim(form) {
        this.fixClaimForm = withDeficiencyResponseField(form);
    }

    init_claim_fields(claim, form, claim_deadline) {
        setDeficiencyResponse();
        return super.init_claim_fields(claim, form, claim_deadline);
    }
}

/**
 * Deficiency portal (Fix Claim). Renders the standard claim guard, gates
 * login on ClassMemberStatus, then populates either the standard claim form
 * or the Future Coverage form based on the claim's "Form Type".
 */
export class customFixClaim extends customSubmitClaim {
    init_claim(form) {
        return super.init_claim(withDeficiencyResponseField(form));
    }

    init_claim_fields(claim, form, claim_deadline) {
        setDeficiencyResponse();
        return super.init_claim_fields(claim, form, claim_deadline);
    }

    claim_deadline() {
        return createElement("div", { class: "d-none" });
    }

    guard(params, getDeadline) {
        // Skip the "on or before May 5, 2026" scope notice from customSubmitClaim;
        // this portal covers both claim forms.
        // Hide the Generic Claim entry; only existing deficient claims can be fixed.
        const fixParams = {
            ...params,
            form_options: {
                ...params.form_options,
                notice_confirmation: {
                    ...params.form_options.notice_confirmation,
                    body: 'If you received a personalized deficiency notice in the mail or via email with a <strong class="underline">Claim ID</strong> and <strong class="underline">Confirmation Code</strong>, please enter the codes you were provided below to fix your claim deficiencies. <br><br> Please remember to enter the full Claim ID exactly as it appears on your personalized Notice, (i.e. 12345678).',
                    zero_zero_id: "Claim ID",
                    confirmation_code: "Confirmation Code",
                },
                generic_claim: {
                    ...params.form_options.generic_claim,
                    display: false,
                },
            },
        };
        return SubmitClaim.prototype.guard.call(this, fixParams, getDeadline);
    }

    claim_form(params, additionalParams) {
        const formElement = super.claim_form(params, additionalParams);
        formElement.querySelector("#claimFormDeadline")?.remove();
        return formElement;
    }

    /**
     * OTP is disabled for the fix-claim portal; go straight to the
     * (gated) claim form after Notice ID + Confirmation Code login.
     */
    send_otp(form, container, class_member, claim_deadline) {
        this.populate_claim_form(form, container, class_member, claim_deadline);
    }

    populate_claim_form(form, container, class_member, claim_deadline) {
        this.dispatch(class_member, (target, targetForm) => {
            if (target === this) {
                super.populate_claim_form(
                    form,
                    container,
                    class_member,
                    claim_deadline
                );
            } else {
                target.populate_claim_form(
                    targetForm,
                    container,
                    class_member,
                    claim_deadline
                );
            }
        });
    }

    /**
     * Applies deficiency gating, then calls `next` with the form instance
     * (and its form config) that matches the claim's Form Type.
     */
    async dispatch(class_member, next) {
        const error = deficiencyGateError(class_member);
        if (error) {
            modal("Error", error);
            loadingGif(loading, "hide", 250);
            return;
        }

        if (!isFutureCoverageClaim(class_member)) {
            next(this, null);
            return;
        }

        try {
            const futureCoverage = await this.futureCoverageClaim();
            // Carry over the login state set by init_claim()
            Object.assign(futureCoverage.claim.data, {
                zero_zero_id: this.claim.data.zero_zero_id,
                confirmation_code: this.claim.data.confirmation_code,
                claim_id: this.claim.data.claim_id,
            });
            futureCoverage.claim.class_member = this.claim.class_member;
            next(futureCoverage, futureCoverage.fixClaimForm);
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Error loading future coverage claim form", {
                    cause: error,
                })
            );
            this.errorHandler.display_modal(error);
        }
    }

    async futureCoverageClaim() {
        if (!this.futureCoverage) {
            const futureCoverage = new customFixFutureCoverageClaim(
                this.data,
                this.lang,
                this.customInitClaim,
                this.customProcessClaim,
                this.customLoadMember,
                this.customDstProcess,
                this.errorHandler
            );
            await futureCoverage.form();
            this.futureCoverage = futureCoverage;
        }
        return this.futureCoverage;
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new customFixClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
